import Container from './LandingContainer'
import { TbCircleCheck } from 'react-icons/tb'
import { motion } from 'framer-motion'
import type { ReactNode } from 'react'

const PointList = ({ children }: { children: ReactNode }) => {
    return (
        <div className="flex items-center gap-2">
            <TbCircleCheck className="text-xl" />
            <span>{children}</span>
        </div>
    )
}

type Feature = {
    title: string
    description: string
    points: string[]
    image: string
    imageAlt: string
}

const features: Feature[] = [
    {
        title: 'Think before it becomes a task',
        description:
            'Capture meeting notes, rough ideas, hypotheses, links, and draft decisions before they are ready for a task manager or wiki.',
        points: [
            'Raw notes and drafts',
            'Project context in one place',
            'No forced structure upfront',
        ],
        image: '/img/landing/features/hero.png',
        imageAlt: 'Capturing a raw note before it becomes a task',
    },
    {
        title: 'Structure while writing',
        description:
            'Turn parts of a note into checklists as your thinking becomes clearer — without moving the context into a separate tool.',
        points: [
            'Checklists inside notes',
            'Lightweight progress tracking',
            'Structure can evolve over time',
        ],
        image: '/img/landing/features/note.png',
        imageAlt: 'Checklist embedded inside a note',
    },
    {
        title: 'Keep sensitive snippets close',
        description:
            'Store temporary passwords, tokens, keys, and private snippets next to the note where they belong, hidden by default and encrypted client-side.',
        points: [
            'Hidden by default',
            'Client-side encryption',
            'Useful in working context',
        ],
        image: '/img/landing/features/secrets.png',
        imageAlt: 'Encrypted secret field inside a note',
    },
]

const FeatureRow = ({ feature, index }: { feature: Feature; index: number }) => {
    const imageFirst = index % 2 === 1

    return (
        <motion.div
            className="bg-gray-100 dark:bg-gray-800 rounded-3xl py-3 px-2.5 lg:py-6 lg:px-4 overflow-hidden mb-10"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
                duration: 0.3,
                type: 'spring',
                bounce: 0.1,
            }}
            viewport={{ once: true }}
        >
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-4">
                <div className={imageFirst ? 'lg:order-2' : ''}>
                    <h3 className="text-4xl">{feature.title}</h3>
                    <p className="mt-6 max-w-[550px] text-lg">
                        {feature.description}
                    </p>
                    <div className="mt-12 flex flex-col gap-4">
                        {feature.points.map((point) => (
                            <PointList key={point}>{point}</PointList>
                        ))}
                    </div>
                </div>
                <div
                    className={`relative flex justify-center ${imageFirst ? 'lg:order-1' : ''}`}
                >
                    <motion.div
                        className="p-2 border border-gray-200 bg-gray-50 dark:bg-gray-700 dark:border-gray-700 rounded-[32px] w-full max-w-[600px]"
                        whileHover={{ y: -20 }}
                    >
                        <div className="absolute inset-x-0 bottom-0 h-20 w-full bg-gradient-to-b from-transparent via-gray-100 to-gray-100 dark:via-zinc-800/70 dark:to-gray-800 scale-[1.1] pointer-events-none" />
                        <div className="bg-white dark:bg-black dark:border-gray-700 border border-gray-200 rounded-[24px] overflow-hidden max-h-[450px]">
                            <img
                                src={feature.image}
                                alt={feature.imageAlt}
                                className="rounded-[24px] w-full h-auto"
                            />
                        </div>
                    </motion.div>
                </div>
            </div>
        </motion.div>
    )
}

const OtherFeatures = () => {
    return (
        <div id="otherFeatures" className="relative z-20 py-2.5 md:py-10">
            <Container>
                <motion.div
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, type: 'spring', bounce: 0.1 }}
                    viewport={{ once: true }}
                >
                    <motion.h2 className="my-6 text-5xl">
                        Built for thinking before structure
                    </motion.h2>
                    <motion.p className="mx-auto max-w-[600px]">
                        MemoSlate gives you a private place to capture, reshape,
                        and clarify work-in-progress thoughts before they become
                        tasks, documents, or shared decisions.
                    </motion.p>
                </motion.div>
                <div className="mt-20">
                    {features.map((feature, index) => (
                        <FeatureRow
                            key={feature.title}
                            feature={feature}
                            index={index}
                        />
                    ))}
                </div>
            </Container>
        </div>
    )
}

export default OtherFeatures
